import { Alert, Flex, Form, Radio, Space, Typography, Upload, UploadFile } from 'antd';
import { add, formatISO } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import CustomersClientGroupsSelect from '../../../../shared/components/customers-client-groups-select/customers-client-groups-select';
import Modal from '../../../../shared/components/modal/modal';
import { useHasPermission } from '../../../../shared/hooks/use-has-permission';
import {
  clearFormErrors,
  handleServerFormErrors,
  mapServerErrors,
} from '../../../../shared/utils/error-handling/error-handling';
import ClientUsersSelect from '../../../client-users/components/client-users-select/client-users-select';
import { useCreateCustomerSignUrlMutation, useLazyGetCustomerQuery } from '../../../customers/api/customers-api';
import { useGetRequestQuery } from '../../../request/api/request-api';
import { requestPreviewAlertId } from '../../../request/constants/constants';
import { scanRequestAlertId } from '../../../scan-requests/constants/constants';
import { useGetTemplateQuery } from '../../../templates/api/templates-api';
import TemplateSelect from '../../../templates/components/template-select/template-select';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, openModal, setAlert } from '../../../ui/store/ui-slice';
import {
  useGetUserQuery,
  useGetUserSettingsQuery,
  useLazyGetUserIframeUrlQuery,
  usePurgeUserTasksMutation,
  useSaveUserSettingMutation,
} from '../../../user/api/user-api';
import { UserPermissionType } from '../../../user/models/user-permission-type';
import { useCreateBulkCustomerSignRequestMutation, useCreateSignRequestMutation } from '../../api/sign-requests-api';
import { signRequestAlertId, signRequestCreateModalId, signRequestInPortalModalId } from '../../constants/constants';
import { SignCopyType } from '../../models/sign-copy-type';
import { SignRequestDetails } from '../../models/sign-request-details';
import { SignRequestStatus } from '../../models/sign-request-status';
import { SignRequestType } from '../../models/sign-request-type';
import SignRequestsSelect from '../sign-requests-select/sign-requests-select';
import styles from './sign-request-create-modal.module.css';
import { DefaultOptionType } from 'antd/es/select';
import { RcFile } from 'antd/es/upload';
import { UploadIcon } from '../../../../shared/icons/upload-icon';
import { hasPermission } from '../../../../shared/utils/has-permission';

interface SignRequestFormModalContentProps {
  signRequestId?: string;
  requestId?: string;
  isStatusSigned?: boolean;
}

type CreateSignRequestType = {
  'signRequest.type': SignRequestType;
  'signRequest.customer'?: { customers: string[]; groups: string[] };
  'signRequest.clientUser'?: string;
  'signRequest.templates'?: string[];
  'signRequest.name': string;
  withMetadataRelatedSignRequest?: string;
  newVersionRelatedSignRequest?: string;
  type: SignCopyType;
  files: RcFile[];
};

const SignRequestCreateModalContent = ({
  isOpen,
  afterClose,
  signRequestId,
  requestId,
  isStatusSigned,
}: LazyModalComponentProps & SignRequestFormModalContentProps): JSX.Element => {
  const { t } = useTranslation(['sign-requests', 'common']);
  const dispatch = useDispatch();
  const [form] = Form.useForm<CreateSignRequestType>();
  const { data: currentUser } = useGetUserQuery();
  const signType = Form.useWatch('signRequest.type', form);
  const type = Form.useWatch('type', form);
  const withMetadataRelatedSignRequest = Form.useWatch('withMetadataRelatedSignRequest', form);
  const newVersionRelatedSignRequest = Form.useWatch('newVersionRelatedSignRequest', form);
  const formTemplateValue = Form.useWatch('signRequest.templates', form);
  const formClientUserValue = Form.useWatch('signRequest.clientUser', form);
  const [isErrorAlertVisible, setIsErrorAlertVisible] = useState(false);
  const [failedTemplateNames, setFailedTemplateNames] = useState<string[]>([]);
  const [createSignRequest, { isLoading: isCreateSignRequestLoading }] = useCreateSignRequestMutation();
  const [purgeUserTasks, { isLoading: isPurgeUserTasksLoading }] = usePurgeUserTasksMutation();
  const [getUserIframeUrl] = useLazyGetUserIframeUrlQuery();
  const [createCustomerSignUrl] = useCreateCustomerSignUrlMutation();
  const [createBulkCustomerSignRequest] = useCreateBulkCustomerSignRequestMutation();
  const hasPermissionCopyMetadata = useHasPermission(UserPermissionType.CopyMetadata);
  const hasPermissionSelectAllUsers = useHasPermission(UserPermissionType.SelectAllUsers);
  const { data: userSettings } = useGetUserSettingsQuery();
  const [saveSetting] = useSaveUserSettingMutation();
  const [selectedTemplatesObject, setSelectedTemplatesObject] = useState<
    DefaultOptionType | DefaultOptionType[] | undefined
  >([]);
  const [file, setFile] = useState<RcFile | undefined>(undefined);

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { currentData: requestItem, isFetching: isRequestItemFetching } = useGetRequestQuery(requestId!, {
    skip: !requestId,
  });

  const signRequestsWithoutPendig = useMemo(() => {
    return requestItem?.signRequests.filter(signRequest => signRequest.status === SignRequestStatus.Signed) || [];
  }, [requestItem?.signRequests]);

  const [getCustomer, { data: customer }] = useLazyGetCustomerQuery();

  const closeSignRequestCreateModal = () => dispatch(closeModal(signRequestCreateModalId));

  const validateOptionalFile = (_: unknown, files?: RcFile[]) => {
    const selectedFile = files?.[0];

    if (!selectedFile) {
      return Promise.resolve();
    }

    if (selectedFile.size === 0) {
      return Promise.reject(new Error(t('signRequestCreateModal.validationError.emptyFile')));
    }

    if (selectedFile.size >= 10 * 1024 * 1024) {
      return Promise.reject(new Error(t('signRequestCreateModal.validationError.uploadLimitReached')));
    }

    return Promise.resolve();
  };

  const setSignRequestCreateAlertMessage = (alertId: string) => {
    dispatch(
      setAlert({
        id: alertId,
        type: 'success',
        title: t('signRequestCreateModal.success.signRequestCreated'),
      }),
    );
  };

  const formCustomerValue = Form.useWatch('signRequest.customer', form);
  const isSelectedMultipleCustomers =
    formCustomerValue && (formCustomerValue.customers.length > 1 || formCustomerValue.groups.length);
  const isTemplateSelected = !!formTemplateValue?.length;
  const isCustomerSelected = !!(formCustomerValue?.customers.length || formCustomerValue?.groups.length);
  const isSubmitDisabled =
    !isTemplateSelected ||
    (signType === SignRequestType.User && !formClientUserValue) ||
    (signType === SignRequestType.Customer && !isCustomerSelected) ||
    (!!signRequestId && type === SignCopyType.WithMetadata && !withMetadataRelatedSignRequest) ||
    (!!signRequestId && type === SignCopyType.NewVersion && !newVersionRelatedSignRequest);

  const signRequest = useMemo(
    () => requestItem?.signRequests.find(signRequest => newVersionRelatedSignRequest === signRequest.id),
    [newVersionRelatedSignRequest, requestItem?.signRequests],
  );
  const { data: template, isFetching: isTemplateFetching } = useGetTemplateQuery(signRequest?.template?.code || '');

  useEffect(() => {
    if (type === SignCopyType.NewVersion && template) {
      form.setFieldValue('signRequest.templates', [template.id]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, type]);

  const initialValues: CreateSignRequestType = useMemo(
    () => ({
      'signRequest.type': SignRequestType.User,
      'signRequest.customer': undefined,
      'signRequest.clientUser': userSettings?.['lastUsedUser'] || currentUser?.id || '',
      'signRequest.templates': undefined,
      'signRequest.name': '',
      type: SignCopyType.NewDocument,
      withMetadataRelatedSignRequest:
        signRequestId && isStatusSigned ? signRequestId : signRequestsWithoutPendig[0]?.id,
      newVersionRelatedSignRequest: signRequestId && isStatusSigned ? signRequestId : signRequestsWithoutPendig[0]?.id,
      files: [],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentUser?.id, isStatusSigned, signRequestId, userSettings],
  );

  const onSuccessCreateSignRequest = () => {
    setSignRequestCreateAlertMessage(signRequestAlertId);
    setSignRequestCreateAlertMessage(requestPreviewAlertId);
    setSignRequestCreateAlertMessage(scanRequestAlertId);

    closeSignRequestCreateModal();
  };
  const onFinish = async (formData: CreateSignRequestType) => {
    clearFormErrors(form);
    setIsErrorAlertVisible(false);
    setFailedTemplateNames([]);

    const {
      'signRequest.type': signRequestType,
      'signRequest.customer': signRequestCustomer,
      'signRequest.templates': signRequestTemplates,
      'signRequest.clientUser': signRequestClientUser,
    } = formData;

    const isPortal = signRequestType === SignRequestType.Portal;
    const isCustomer = signRequestType === SignRequestType.Customer;
    const isCopyNewVersion = formData.type === SignCopyType.NewVersion;

    if (!signRequestTemplates?.length) {
      return;
    }

    if (isPortal) {
      await purgeUserTasks();
    }

    if (
      isCustomer &&
      signRequestCustomer &&
      (signRequestCustomer?.groups.length || signRequestCustomer?.customers.length > 1)
    ) {
      const payload = {
        customers: signRequestCustomer.customers || [],
        groups: signRequestCustomer.groups || [],
        template: signRequestTemplates?.[0] || '',
        linkExpiresAt: formatISO(add(new Date(), { days: 7 })),
        copyMetadata: [SignCopyType.WithMetadata, SignCopyType.NewVersion].includes(formData.type),
        ...(formData.type === SignCopyType.WithMetadata && {
          relatedSignRequest: formData.withMetadataRelatedSignRequest,
        }),
      };

      createBulkCustomerSignRequest(payload)
        .then(() => onSuccessCreateSignRequest())
        .catch(error => {
          handleServerFormErrors(
            form,
            mapServerErrors(error.data?.errors, {
              customers: 'signRequest.customer',
              groups: 'signRequest.customer',
              template: 'signRequest.templates',
            }),
          );
        });
    } else {
      await Promise.all(
        (signRequestTemplates || []).map(template => {
          const payload = {
            signRequest: {
              type: isPortal ? SignRequestType.User : signRequestType,
              ...(signRequestClientUser && { clientUser: signRequestClientUser }),
              ...(isPortal && { clientUser: currentUser?.id }),
              ...(signRequestCustomer && { customer: signRequestCustomer?.customers[0] }),
              ...(signRequestTemplates && { template }),
              ...(requestId && { request: requestId }),
            },
            copyMetadata: [SignCopyType.WithMetadata, SignCopyType.NewVersion].includes(formData.type),
            copyContent: isCopyNewVersion,
            hideOldFields: isCopyNewVersion,
            ...(formData.type === SignCopyType.WithMetadata && {
              relatedSignRequest: formData.withMetadataRelatedSignRequest,
            }),
            ...(isCopyNewVersion && {
              relatedSignRequest: formData.newVersionRelatedSignRequest,
            }),
            ...(formData.files && { file: file }),
          };

          return createSignRequest(payload)
            .unwrap()
            .then(response => {
              if (isPortal) {
                getUserIframeUrl(undefined, false)
                  .unwrap()
                  .then(iframeUrl => dispatch(openModal({ id: signRequestInPortalModalId, props: { iframeUrl } })));
              }

              if (isCustomer && signRequestCustomer) {
                if (customer) {
                  createCustomerSignUrl({
                    customerId: customer.id,
                    email: customer.email,
                    phone: customer.phone,
                    signRequestId: (response as SignRequestDetails).id,
                    linkExpiresAt: formatISO(add(new Date(), { days: 7 })),
                  })
                    .unwrap()
                    .catch(error => handleServerFormErrors(form, error.data?.errors));
                }
              }
            })
            .catch(error => {
              const failedTemplateName = selectedTemplatesObject?.find(
                (selectedTemplate: DefaultOptionType) => selectedTemplate.value === template,
              ).children;
              setFailedTemplateNames(prevState => {
                if (!prevState.includes(failedTemplateName)) {
                  return [failedTemplateName, ...prevState];
                }
                return prevState;
              });
              throw error;
            });
        }),
      )
        .then(() => {
          if (signRequestClientUser && userSettings?.['lastUsedUser'] !== signRequestClientUser) {
            saveSetting({
              name: 'lastUsedUser',
              value: signRequestClientUser,
            });
          }

          onSuccessCreateSignRequest();
        })
        .catch(error => {
          handleServerFormErrors(
            form,
            mapServerErrors(error.data?.errors || [], {
              relatedSignRequest:
                formData.type === SignCopyType.WithMetadata
                  ? 'withMetadataRelatedSignRequest'
                  : 'newVersionRelatedSignRequest',
            }),
          );
          const allFormErrorsEmpty = form.getFieldsError().every(obj => obj.errors.length === 0);

          if (allFormErrorsEmpty) {
            setIsErrorAlertVisible(true);
          }
        });
    }
  };

  useEffect(() => {
    form.resetFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  useEffect(() => {
    if (formCustomerValue && formCustomerValue.customers.length === 1) {
      getCustomer(formCustomerValue.customers[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formCustomerValue]);

  return (
    <Modal
      title={t('signNewDocument')}
      isOpen={isOpen}
      afterClose={afterClose}
      isLoading={isCreateSignRequestLoading || isPurgeUserTasksLoading || isTemplateFetching}
      onCancel={closeSignRequestCreateModal}
      formId={signRequestCreateModalId}
      width={960}
      disableSubmitBtn={isSubmitDisabled}
    >
      {isErrorAlertVisible && (
        <Alert
          className={styles.errorAlert}
          message={
            failedTemplateNames ? (
              <Trans
                i18nKey="signRequestCreateModal.validationError.templateError"
                t={t}
                ns="sign-requests"
                values={{ name: failedTemplateNames.join(', ') }}
              />
            ) : (
              t('signRequestCreateModal.validationError.unexpectedError')
            )
          }
          type="error"
          showIcon
        />
      )}
      <Form
        id={signRequestCreateModalId}
        form={form}
        initialValues={initialValues}
        layout="vertical"
        onFinish={onFinish}
      >
        <Form.Item name="signRequest.type" label={t('signRequestCreateModal.label.sign')}>
          <Radio.Group className={styles.radioGroup}>
            <Space direction="vertical">
              <Radio value={SignRequestType.User}>{t('signRequestCreateModal.label.signInApp')}</Radio>
              <Radio value={SignRequestType.Portal}>{t('signRequestCreateModal.label.signInPortal')}</Radio>
              {currentUser && hasPermission(currentUser, UserPermissionType.ViewCustomers) && (
                <Radio value={SignRequestType.Customer}>{t('signRequestCreateModal.label.signViaEmail')}</Radio>
              )}
            </Space>
          </Radio.Group>
        </Form.Item>
        {signType === SignRequestType.User && (
          <Form.Item
            name="signRequest.clientUser"
            label={t('label.appUser')}
            rules={[{ required: true, message: t('errors.required', { ns: 'common' }) }]}
          >
            <ClientUsersSelect
              placeholder={t('signScanRequest.label.selectUser', { ns: 'common' })}
              disabled={!hasPermissionSelectAllUsers}
            />
          </Form.Item>
        )}
        {signType === SignRequestType.Customer && (
          <Form.Item
            name="signRequest.customer"
            label={t('label.customer', { ns: 'common' })}
            rules={[
              {
                validator: (_, value?: CreateSignRequestType['signRequest.customer']) =>
                  value?.customers.length || value?.groups.length
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('errors.required', { ns: 'common' }))),
              },
            ]}
          >
            <CustomersClientGroupsSelect
              placeholder={t('signRequestCreateModal.label.choose')}
              mode="multiple"
              maxTagCount={4}
            />
          </Form.Item>
        )}
        {signRequestId && (
          <Form.Item name="type" label={t('signRequestCreateModal.label.type')}>
            <Radio.Group className={styles.radioGroup}>
              <Space direction="vertical" className={styles.radioWrapper}>
                <div>
                  <Radio value={SignCopyType.NewDocument}>{t('signRequestCreateModal.label.createNewDoc')}</Radio>
                  <div className={styles.signCopyTypeRadio}>
                    <Typography.Text type="secondary">{t('signRequestCreateModal.hint.createNewDoc')}</Typography.Text>
                  </div>
                  {!hasPermissionCopyMetadata && (
                    <Alert
                      message={t('signRequestCreateModal.hint.noPermissionCopySignRequest')}
                      type="info"
                      showIcon
                    />
                  )}
                </div>
                <div>
                  <Radio
                    value={SignCopyType.WithMetadata}
                    disabled={!hasPermissionCopyMetadata || !signRequestsWithoutPendig.length}
                  >
                    {t('signRequestCreateModal.label.createWithMetadata')}
                  </Radio>
                  <div className={styles.signCopyTypeRadio}>
                    <Typography.Text type="secondary">
                      {t('signRequestCreateModal.hint.createWithMetadata')}
                    </Typography.Text>
                  </div>
                  {hasPermissionCopyMetadata && (
                    <Form.Item name="withMetadataRelatedSignRequest" className={styles.nestedFormItem}>
                      <SignRequestsSelect
                        signRequests={signRequestsWithoutPendig}
                        isRequestItemFetching={isRequestItemFetching}
                        disabled={type !== SignCopyType.WithMetadata}
                        placeholder={t('signRequestCreateModal.label.choose')}
                      />
                    </Form.Item>
                  )}
                </div>
                <div>
                  <Radio
                    value={SignCopyType.NewVersion}
                    disabled={
                      !hasPermissionCopyMetadata || !!isSelectedMultipleCustomers || !signRequestsWithoutPendig?.length
                    }
                  >
                    {t('signRequestCreateModal.label.createNewVersion')}
                  </Radio>
                  <div className={styles.signCopyTypeRadio}>
                    <Typography.Text type="secondary">
                      {t('signRequestCreateModal.hint.createNewVersion')}
                    </Typography.Text>
                  </div>
                  {hasPermissionCopyMetadata && (
                    <Form.Item name="newVersionRelatedSignRequest" className={styles.nestedFormItem}>
                      <SignRequestsSelect
                        signRequests={signRequestsWithoutPendig || []}
                        isRequestItemFetching={isRequestItemFetching}
                        placeholder={t('signRequestCreateModal.label.choose')}
                        disabled={type !== SignCopyType.NewVersion}
                      />
                    </Form.Item>
                  )}
                </div>
              </Space>
            </Radio.Group>
          </Form.Item>
        )}
        <Form.Item
          name="signRequest.templates"
          label={t('label.template', { ns: 'common' })}
          rules={[
            {
              validator: (_, value?: string[]) =>
                value?.length ? Promise.resolve() : Promise.reject(new Error(t('errors.required', { ns: 'common' }))),
            },
          ]}
        >
          <TemplateSelect
            placeholder={t('signRequestCreateModal.label.choose')}
            mode="multiple"
            maxTagCount={4}
            maxCount={
              signType === SignRequestType.Customer ||
              signType === SignRequestType.Portal ||
              type === SignCopyType.NewVersion
                ? 1
                : 10
            }
            onChange={(value, option) => setSelectedTemplatesObject(option)}
          />
        </Form.Item>
        {type !== SignCopyType.NewVersion && (
          <Form.Item
            name="files"
            getValueFromEvent={({ fileList }: { fileList: UploadFile[] }) =>
              fileList.map(({ originFileObj }) => originFileObj).filter(Boolean) as RcFile[]
            }
            rules={[{ validator: validateOptionalFile }]}
          >
            <Upload.Dragger
              maxCount={1}
              onChange={({ fileList }) => setFile(fileList[0]?.originFileObj)}
              onRemove={() => setFile(undefined)}
              beforeUpload={() => false}
              accept=".pdf"
            >
              <Flex gap="small" justify="center">
                <UploadIcon className={styles.uploadIcon} />
                <Typography.Text className={styles.subtitle}>
                  {t('fileUpload.uploadDescriptionOnlyPdf', { ns: 'common' })}
                </Typography.Text>
              </Flex>
            </Upload.Dragger>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
};

const SignRequestCreateModal = (): JSX.Element => {
  return <LazyModal modalId={signRequestCreateModalId} Component={SignRequestCreateModalContent} />;
};

export default SignRequestCreateModal;
